<template>
  <div
    class="py-6 px-6 bg-pure-white rounded-xl"
    :style="{ height: '22.5rem', width }"
  >
    <div
      class="mb-1 flex items-center justify-between"
      title="Therapists with highest cancellation"
    >
      <BlTextTitle4
        text="Therapists with highest cancellation"
        class="text-slate-900 truncate"
      />
      <!-- <router-link
        :to="
          `/analytics/cancellation-overview?start_date=${dateInput.model[0].start_date}&end_date=${dateInput.model[0].end_date}&group_by=user`
        "
      >
        <BlInputButton type="outlined" dense label="Details" />
      </router-link> -->
    </div>
    <div class="mb-5 flex">
      <BlInputFilter
        :activeLabel="dateActiveLabel"
        title="Date"
        :defaultValue="[defaultDate]"
        :input="dateInput"
        @changed="onChangeHandler"
      />
    </div>
    <div class="mb-2">
      <BlTextSubtitle2
        text="Therapists by cancellation hours"
        class="text-slate-700"
      />
    </div>
    <div v-if="fetchLoading" class="h-48 grid place-content-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="!graphData || !barChartData.labels.length"
      class="h-48 grid flex-col place-content-center text-center"
    >
      <BlTextBody2 :text="emptyState.title" class="px-4 font-semibold" />
    </div>
    <div v-else class="flex flex-col">
      <div class="relative">
        <BarChart
          :labels="barChartData.labels"
          :datasets="barChartData.datasets"
          stacked
          horizontal
          hideXAxes
          height="10.875rem"
          :width="`calc(${width} - 3rem - 2.375rem)`"
        />
        <div
          class="absolute top-0 right-0 flex flex-col justify-evenly"
          :style="{ height: '10.875rem' }"
        >
          <div
            v-for="(n, idx) in barChartData.totals"
            :key="idx"
            class="h-5.5 flex items-center flex-shrink-0"
          >
            <BlTextBody2 :text="n" />
          </div>
        </div>
      </div>
      <div
        v-if="barChartPageCount > 1"
        class="flex items-center justify-center"
      >
        <div
          v-for="n in barChartPageCount"
          :key="n"
          @click="changeBarChartPage(n)"
          class="p-1 cursor-pointer"
        >
          <div
            :class="[barChartPage === n ? 'bg-slate-700' : 'bg-slate-500']"
            class="h-2.5 w-2.5 flex-shrink-0 rounded-full hover:bg-slate-700 transition-all duration-300"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BarChart from "../graphs/BarChart.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTooltip from "../../BlTooltip.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputSelect from "../../BlInputSelect.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "CenterOverviewTherapistsWithHighestCancellation",
  components: {
    BarChart,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTooltip,
    BlIcon,
    BlInputFilter,
    BlInputSelect,
    BlInputButton
  },
  props: {
    width: { type: String, default: "25rem" }
  },
  data() {
    const interval = "month";
    const defaultDate = {
      start_date: moment()
        .startOf(interval)
        .format("YYYY-MM-DD"),
      end_date: moment()
        .endOf(interval)
        .format("YYYY-MM-DD")
    };
    return {
      defaultDate,
      /** */
      fetchLoading: false,
      graphData: null,
      /** */
      emptyState: {
        title: "No appointments recorded yet",
        desc: [
          "It looks like you've selected a future date range.",
          "Since no appointments have been cancelled yet, there's no data to display.",
          "Try selecting a date range that includes past or current dates to view your appointment history."
        ]
      },
      /** */
      dateInput: {
        model: [defaultDate],
        tabs: [{ name: "Date", kind: "date" }],
        dense: true
      },
      barChartPage: 1,
      barChartPerPage: 5
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    searchParams() {
      return this.$route.query || {};
    },
    dateActiveLabel() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;

      const sYYYY = moment(start).format("YYYY");
      const sMMM = moment(start).format("MMM");
      const sDD = moment(start).format("DD");

      const eYYYY = moment(end).format("YYYY");
      const eMMM = moment(end).format("MMM");
      const eDD = moment(end).format("DD");

      let date = "";
      if (sYYYY === eYYYY) {
        if (sMMM === eMMM) date = `${sDD} - ${eDD} ${sMMM}, ${sYYYY}`;
        else if (sMMM === "Jan" && eMMM === "Dec") date = `${sYYYY}`;
        else date = `${sDD} ${sMMM} - ${eDD} ${eMMM}, ${sYYYY}`;
      } else date = `${sYYYY} - ${eYYYY}`;
      return `
        <span class="flex items-center gap-2">
          <span>Date</span>
          <b>${date}</b>
        </span>
      `;
    },
    /*
    "data": {
      "users": [
        {
          "id": 1,
          "user_name": "Admin 1",
          "role": "admin",
          "clients_handled": 2,
          "total_by_session": 4,
          "total_by_hours": 3.5,
          "client_by_session": 1,
          "therapist_by_session": 1,
          "clinic_or_admin_by_session": 2,
          "client_by_hours": 1.0,
          "therapist_by_hours": 1.0,
          "clinic_or_admin_by_hours": 1.5
        }
      ],
      "total_count": 2
    }
    */
    barChartData() {
      const n = { labels: [], datasets: [], legends: [], totals: [], empty: true };
      if (!this.graphData) return n;
      const groupBy = "user";
      const raw = this.graphData.data[`${groupBy}s`];
      if (!raw) return n;
      const keys = [
        "client_by_hours",
        "therapist_by_hours",
        "clinic_or_admin_by_hours"
      ];
      const colors = ["#F63D68", "#FEA3B4", "#FFE4E8"];
      const texts = ["By client", "By therapist", "By admin/clinic"];

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        label: texts[idx],
        data: [],
        color: colors[idx]
      }));
      const legends = keys.map((key, idx) => ({
        label: texts[idx],
        color: colors[idx]
      }));
      const totals = [];
      let empty = true;

      raw.forEach(i => {
        labels.push(i[[`${groupBy}_name`]]);
        let t = 0;
        keys.forEach((key, idx) => {
          const value = i[key] || 0;
          datasets[idx].data.push(value);
          t += value;
          if (value) empty = false;
        });
        totals.push(t);
      });

      return { labels, datasets, legends, totals, empty };
    },
    barChartDataTotalCount() {
      if (!this.graphData) return 0;
      return this.graphData.data.total_count || 0;
    },
    barChartPageCount() {
      const dataLength = this.barChartDataTotalCount;
      return Math.ceil(dataLength / this.barChartPerPage) || 1;
    }
  },
  mounted() {
    this.fetchGraph();
  },
  methods: {
    ...mapActions(["actionGet"]),
    fetchGraph() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;
      let params = `?start_date=${start}&end_date=${end}&group_by=user`;
      params += `&page=${this.barChartPage}&per_page=${this.barChartPerPage}`;
      if (this.searchParams["branch"]) {
        params += `&branch_ids=${this.searchParams["branch"]}`;
      }
      const data = {
        url: `/api/v1/analytics/cancellation_overview_graph${params}`
      };
      this.fetchLoading = true;
      this.graphData = null;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchLoading = false;
        if (!success) return;
        this.graphData = data;
      });
    },
    onChangeHandler(payload) {
      payload.input.model = payload.value;
      this.barChartPage = 1;
      this.fetchGraph();
    },
    changeBarChartPage(page) {
      if (page === this.barChartPage) return;
      this.barChartPage = page;
      this.fetchGraph();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/analytics/mainDashboard/TherapistsWithHighestCancellation.vue